import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Paperclip, Clock, Sparkles } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { studentNav } from "../../components/shared/navConfig";
import api from "../../lib/api";

const STATUS_TONE = {
  sent:        "#3B82F6", accepted:  "#10B981", rejected:  "#F43F5E",
  in_progress: "#06B6D4", submitted: "#EAB308", revision:  "#F97316",
  approved:    "#10B981", completed: "#10B981", archived:  "#64748B",
};

const fmtRel = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function StudentDirectWork() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/direct-work/mine");
      setRows(data);
    } catch (_) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <DashboardShell nav={studentNav} title="My Direct Work" subtitle="Direct assignments from Assisto4U Admin.">
      {loading ? (
        <div className="text-sm py-10 text-center" style={{ color: "var(--text-dim)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card-surface p-10 text-center">
          <Sparkles size={22} className="mx-auto mb-3" style={{ color: "var(--brand)" }}/>
          <div className="text-sm mb-1" style={{ color: "var(--text)" }}>No direct assignments yet.</div>
          <div className="text-xs" style={{ color: "var(--text-dim)" }}>
            Admin will reach out here when a task, proposal or assignment is created for you.
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map(r => (
            <Link
              key={r.direct_work_id}
              to={`/student/direct-work/${r.direct_work_id}`}
              data-testid={`sdw-card-${r.direct_work_id}`}
              className="card-surface card-hover p-4 block"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono" style={{ color: "var(--text-dim)" }}>{r.direct_work_id}</div>
                  <div className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>{r.title}</div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full border"
                      style={{ borderColor: STATUS_TONE[r.status] + "55", color: STATUS_TONE[r.status], background: STATUS_TONE[r.status] + "10" }}>
                  {r.status}
                </span>
              </div>
              <div className="text-xs mb-3 truncate" style={{ color: "var(--text-muted)" }}>
                {r.admin_name || "Assisto4U Admin"} · {r.city || r.location || "Remote"}
              </div>
              <div className="flex items-center justify-between text-[11px]" style={{ color: "var(--text-dim)" }}>
                <span className="inline-flex items-center gap-1"><Clock size={11}/> {r.deadline || "no deadline"}</span>
                <span>Last update {fmtRel(r.last_message_at)}</span>
              </div>
              {r.unread_student > 0 && (
                <div className="mt-2 flex items-center justify-between text-[11px]">
                  <span className="inline-flex items-center gap-1" style={{ color: "var(--text-dim)" }}>
                    <MessageSquare size={11}/> New messages
                  </span>
                  <span className="px-1.5 py-0.5 rounded bg-rose-500/10 border border-rose-500/40 text-rose-500">
                    {r.unread_student}
                  </span>
                </div>
              )}
              {r.attachment_file_ids?.length > 0 && (
                <div className="mt-2 text-[11px] inline-flex items-center gap-1" style={{ color: "var(--text-dim)" }}>
                  <Paperclip size={11}/> {r.attachment_file_ids.length} attachments
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
